import {
  GenerateApiPopoverButton,
  GenerateApiPopoverButtonProps,
} from 'components/generate-api-popover/generate-api-popover-button'
import { observer } from 'mobx-react-lite'
import * as React from 'react'
import { Button, Tooltip } from 'src/design-system'
import { store } from 'store/index'
import { errorToast } from '../../utils/error-toast'
import { successToast } from '../../utils/success-toast'
import { ChevronLeft, ChevronRight } from '@easy-eva-icons/react'
import { AsyncTaskOutput } from 'store/modules/async-tasks'
import { AiVmMap, AiVmMapKey } from './content/map'
import { AiPopoverVm } from './popover.types'
import { AiGenerateButtonVm } from './ai-generate-button-vm'
import cn from 'classnames'

type AiGenerateButtonProps = {
  source: string
  onUse: (output: AsyncTaskOutput) => void | Promise<void>
  clearTaskListOnUse?: boolean
  contentType: AiVmMapKey
  inputs: Record<string, unknown>
  className?: string
  children?: string
  popoverContentProps?: GenerateApiPopoverButtonProps['popoverContentProps']
  tooltipContent?: string
  enablePreview?: boolean
  naked?: boolean
  disabled?: boolean
}

export const AiGenerateButton = observer((props: AiGenerateButtonProps) => {
  const {
    source,
    onUse,
    contentType,
    inputs,
    className,
    children,
    clearTaskListOnUse = true,
    popoverContentProps,
    tooltipContent,
    enablePreview = true,
    naked = false,
    disabled = false,
  } = props

  const aiVm = React.useMemo(() => {
    return new AiVmMap[contentType](source)
  }, [contentType, source])

  const [popoverContentLoading, setPopoverContentLoading] =
    React.useState(false)

  const [activeTaskId, setActiveTaskId] = React.useState<string | null>(null)
  const [asyncTaskList, setAsyncTaskList] = React.useState<string[]>([])

  // Identifies the generation currently worth listening to. See startGeneration.
  const generationRunIdRef = React.useRef(0)

  const vm = React.useMemo(() => {
    return new AiGenerateButtonVm(activeTaskId, asyncTaskList)
  }, [activeTaskId, asyncTaskList])

  // Handles the enablePreview=false path: poll once, and if it already
  // succeeded, use the output immediately instead of showing the preview
  // popover. Returns true when it fully handled the generation (caller
  // should stop), false when the normal preview flow should take over.
  const finishWithoutPreview = async (
    taskId: string,
    superseded: () => boolean,
  ): Promise<boolean> => {
    setActiveTaskId(taskId)

    await store.asyncTasks.byId(taskId)?.pollOne()
    if (superseded()) return true

    const task = store.asyncTasks.byId(taskId)
    if (!task?.isSuccess) return false

    await onUse(task.output)
    if (superseded()) return true

    setPopoverContentLoading(false)
    if (clearTaskListOnUse) setAsyncTaskList([])
    return true
  }

  const startGeneration = async (
    additionalInputs: Record<string, unknown> | null,
  ) => {
    // Cancelling doesn't stop the request that's already in flight — it can
    // only stop us acting on the answer when it lands. Each run takes a ticket,
    // and cancelling (or starting another run) bumps the counter, so a run that
    // is no longer the current one bails instead of toasting "generated!" over
    // the top of a cancellation.
    const runId = ++generationRunIdRef.current
    const superseded = () => runId !== generationRunIdRef.current

    setPopoverContentLoading(true)

    const taskId = await aiVm.onSubmit({
      ...inputs,
      ...additionalInputs,
    })

    if (superseded()) return

    if (!taskId) {
      setPopoverContentLoading(false)
      return
    }

    if (!enablePreview && (await finishWithoutPreview(taskId, superseded))) {
      return
    }

    setAsyncTaskList((prev) => [...prev, taskId])

    await store.asyncTasks.byId(taskId)?.pollOne()
    if (superseded()) return

    const asyncTask = store.asyncTasks.byId(taskId)

    setActiveTaskId(taskId)

    if (asyncTask?.isSuccess) {
      successToast(aiVm.onSuccessMessage)
    } else {
      errorToast(aiVm.onErrorMessage)
    }

    setPopoverContentLoading(false)
  }

  const onUseClick = () => {
    if (vm.activeTask?.isSuccess) {
      vm.trackAiEvent('accepted', source, {
        attempts: asyncTaskList.length,
        attemptSelected: vm.activeTaskIndex + 1,
      })

      onUse(vm.activeTask?.output)

      if (clearTaskListOnUse) setAsyncTaskList([])
    }
  }

  return (
    <div className={cn(className)}>
      <Tooltip content={tooltipContent} disabled={!tooltipContent}>
        <div className={cn({ 'opacity-50 pointer-events-none': disabled })}>
          <GenerateApiPopoverButton
            naked={naked}
            loading={popoverContentLoading}
            generatingMessage={aiVm.generatingMessage}
            onCancel={() => {
              // Retires the in-flight run so its result can't report success
              // after the user has already backed out.
              generationRunIdRef.current += 1

              vm.trackAiEvent('cancelled', source, {
                attempts: asyncTaskList.length,
              })
              setPopoverContentLoading(false)
              setAsyncTaskList([])
              // Every view model implements AiPopoverVm, but the concrete
              // union only surfaces members all of them declare — and this one
              // is optional, so read it through the interface.
              successToast(
                (aiVm as AiPopoverVm).onCancelMessage ??
                  'Generation cancelled.',
              )
            }}
            onClickTrigger={() => {
              if (!aiVm.formContent) {
                startGeneration({})
              }
            }}
            popoverContentProps={popoverContentProps}
            popoverContent={({ onClose }) => (
              <div className="z-tooltip relative">
                {vm.hasFailed && (
                  <div className="flex justify-between items-center text-center space-x-2 pb-4 text-gray-700">
                    {aiVm.onErrorMessage}
                  </div>
                )}
                {vm.hasSucceeded && (
                  <>
                    {vm.activeTask?.output &&
                      aiVm.content(vm.activeTask?.output)}
                    <div className="flex justify-between mt-6">
                      <div className="flex justify-between items-center space-x-2">
                        <Button
                          variant="outline"
                          disabled={!vm.previousTaskId}
                          onClick={() => {
                            vm.trackAiEvent('previous', source, {
                              attempts: asyncTaskList.length,
                            })
                            vm.previousTaskId &&
                              setActiveTaskId(vm.previousTaskId)
                          }}
                        >
                          <span className="sr-only">View previous content</span>
                          <ChevronLeft aria-hidden />
                        </Button>
                        <span className="text-xs text-gray-500">
                          {vm.activeTaskIndex + 1} of {asyncTaskList.length}
                        </span>
                        <Button
                          variant="outline"
                          disabled={!vm.nextTaskId}
                          onClick={() => {
                            vm.trackAiEvent('next', source, {
                              attempts: asyncTaskList.length,
                            })
                            vm.nextTaskId && setActiveTaskId(vm.nextTaskId)
                          }}
                        >
                          <span className="sr-only">View next content</span>
                          <ChevronRight aria-hidden />
                        </Button>
                      </div>

                      <div className="flex space-x-2">
                        <Button
                          colourVariant="paper"
                          onClick={() => {
                            vm.trackAiEvent('try-again', source, {
                              attempts: asyncTaskList.length,
                            })
                            startGeneration({})
                          }}
                        >
                          Try again
                        </Button>
                        <Button
                          className="flex items-center"
                          onClick={() => {
                            onUseClick()
                            onClose()
                          }}
                        >
                          Use
                        </Button>
                      </div>
                    </div>
                  </>
                )}
                {vm.canGenerate && !popoverContentLoading && (
                  <>
                    {!aiVm.formContent && (
                      <Button
                        className="w-full max-w-none"
                        type="button"
                        onClick={() => startGeneration({})}
                      >
                        {aiVm.generateButtonText}
                      </Button>
                    )}
                    {aiVm.formContent && (
                      <aiVm.formContent
                        inputs={inputs}
                        startGeneration={startGeneration}
                      />
                    )}
                  </>
                )}
              </div>
            )}
            disabled={disabled}
          >
            {children}
          </GenerateApiPopoverButton>
        </div>
      </Tooltip>
    </div>
  )
})
